import { useState } from 'react';
import { useAppStore } from '@/store/AppStore';
import { formatTL } from '@/lib/format';
import { jobPaid, jobRemaining } from '@/lib/finance';
import Badge from '@/components/base/Badge';
import JobDetail from '@/components/feature/JobDetail';
import EmptyState from '@/components/base/EmptyState';
import { SERVICES, REGIONS } from '@/mocks/constants';
import type { Job } from '@/types';

export default function TamamlananIsler() {
  const { jobs, payments, teams } = useAppStore();
  const [selected, setSelected] = useState<Job | null>(null);
  const [search, setSearch] = useState('');
  const [service, setService] = useState('');
  const [region, setRegion] = useState('');
  const [team, setTeam] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');

  const completed = jobs.filter((j) => j.status === 'Kapandı');

  const filtered = completed.filter((j) => {
    if (search && !j.customerName.toLowerCase().includes(search.toLowerCase())) return false;
    if (service && j.service !== service) return false;
    if (region && !j.location.includes(region)) return false;
    if (team && j.team !== team) return false;
    if (paymentFilter === 'Tam Tahsil' && jobRemaining(j, payments) > 0) return false;
    if (paymentFilter === 'Kalan Var' && jobRemaining(j, payments) === 0) return false;
    return true;
  });

  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      <div>
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-accent-600">
          <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
          Müşteri
        </div>
        <h1 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Tamamlanan İşler</h1>
        <p className="mt-1 text-sm text-foreground-500">Kapanan işlerin geçmiş arşivi.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          className="h-10 w-full rounded-md border border-background-200 bg-white px-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none sm:w-56"
          placeholder="Müşteri ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="h-10 rounded-md border border-background-200 bg-white px-3 text-sm" value={service} onChange={(e) => setService(e.target.value)}>
          <option value="">Tüm Hizmetler</option>
          {SERVICES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select className="h-10 rounded-md border border-background-200 bg-white px-3 text-sm" value={region} onChange={(e) => setRegion(e.target.value)}>
          <option value="">Tüm Bölgeler</option>
          {REGIONS.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <select className="h-10 rounded-md border border-background-200 bg-white px-3 text-sm" value={team} onChange={(e) => setTeam(e.target.value)}>
          <option value="">Tüm Ekipler</option>
          {teams.map((t) => (
            <option key={t.id} value={t.name}>{t.name}</option>
          ))}
        </select>
        <select className="h-10 rounded-md border border-background-200 bg-white px-3 text-sm" value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
          <option value="">Tüm Ödemeler</option>
          <option value="Tam Tahsil">Tam Tahsil</option>
          <option value="Kalan Var">Kalan Var</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-background-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-background-100 text-xs font-semibold uppercase tracking-wide text-foreground-400">
                <th className="px-5 py-3">Müşteri</th>
                <th className="px-5 py-3">Hizmet</th>
                <th className="px-5 py-3">Tamamlanma</th>
                <th className="px-5 py-3">Ekip</th>
                <th className="px-5 py-3">İş Bedeli</th>
                <th className="px-5 py-3">Tahsil</th>
                <th className="px-5 py-3">Kalan</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((job) => {
                const paid = jobPaid(payments, job.id);
                const remaining = jobRemaining(job, payments);
                return (
                  <tr
                    key={job.id}
                    onClick={() => setSelected(job)}
                    className="cursor-pointer border-b border-background-100 transition-colors hover:bg-background-50"
                  >
                    <td className="px-5 py-3.5 font-semibold text-foreground-900">{job.customerName}</td>
                    <td className="px-5 py-3.5 text-foreground-600">{job.service}</td>
                    <td className="px-5 py-3.5 text-foreground-500">{job.date}</td>
                    <td className="px-5 py-3.5 text-foreground-600">{job.team}</td>
                    <td className="px-5 py-3.5 font-semibold text-foreground-900">{formatTL(job.jobValue)}</td>
                    <td className="px-5 py-3.5 font-semibold text-emerald-600">{formatTL(paid)}</td>
                    <td className="px-5 py-3.5">
                      {remaining > 0 ? (
                        <Badge tone="amber">{formatTL(remaining)}</Badge>
                      ) : (
                        <span className="text-xs font-semibold text-foreground-400">Yok</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <EmptyState
            icon="ri-check-double-line"
            title="Bu filtrelerle eşleşen tamamlanan iş bulunmuyor."
            description="Filtreleri değiştirerek tekrar deneyin."
          />
        )}
      </div>

      {selected && <JobDetail job={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}